import { EventEmitter, Injectable } from '@angular/core';
import { ISAPI } from 'is-lib';
import { GenericService, LoaderConfig, ErrorConfig } from './generic.service';
import { environment } from 'src/environments/environment';
import { SignalRService } from './signalR.service';
import { Alert, AlertWithTimediff } from '../models/Alert';
import { BehaviorSubject, Subject, tap } from 'rxjs';
import { ServerResponse } from '../models/ServerResponse';
import { TicketMessage } from '../models/Ticket';

@Injectable({
    providedIn: 'root'
})
export class AlertsService {
    constructor(
        private _httpService: GenericService,
        private _signalRService: SignalRService
    ) {
    }

    public allUserAlerts$: Subject<AlertWithTimediff[]> = new Subject<AlertWithTimediff[]>();
    public messageSubject$: Subject<TicketMessage> = new Subject<TicketMessage>();
    private _allUserAlerts: AlertWithTimediff[] = [];
    private static readonly MAX_STORED_ALERTS = 100;
    private _pendingAlertTimeouts = new Set<ReturnType<typeof setTimeout>>();


    getAlerts(country: string, body: ISAPI, loaderConfig: LoaderConfig | boolean = false, errorConfig?: ErrorConfig | boolean) {
        return this._httpService.post(environment.apiUrl + 'Alerts/' + country + '/Get', body, undefined, loaderConfig, undefined, errorConfig).pipe(
            tap((sr: ServerResponse<Alert[]>) => {
                if (sr.data && sr.data.length > 0) {
                    let timeDiffAlerts = sr.data.map( (alert: Alert) => new AlertWithTimediff(alert))
                    this.allUserAlerts$.next(timeDiffAlerts);
                }
            })
        );
    }

    testNotification(errorConfig?: ErrorConfig | boolean) {
        this._httpService.post(environment.apiUrl + 'Alerts/Test', undefined, undefined, undefined, undefined, errorConfig).subscribe();
    }

    addAlertToList(alert: Alert) {
        let mappedAlert = new AlertWithTimediff(alert, true);

        this._allUserAlerts.push(mappedAlert);

        this._allUserAlerts.sort((current, previous) => -(current.creationDate.getTime() - previous.creationDate.getTime()));

        // Newest first, so the oldest ones are the ones dropped
        if (this._allUserAlerts.length > AlertsService.MAX_STORED_ALERTS) {
            this._allUserAlerts.length = AlertsService.MAX_STORED_ALERTS;
        }

        this.allUserAlerts$.next(this._allUserAlerts);
    }

    listenToNewAlerts() {
        this._signalRService.startConnection();

        this._signalRService.addMessageListener('NewAlert', (user: string, message: string, alert: Alert) => {

            // SetTimeout is here because without it the change of the subject value does not emit it's value to its subscribers
            this.deferEmission(() => this.addAlertToList(alert));

        });

        this._signalRService.addMessageListener('TicketMessage', (user: string, message: string, ticketMessage: TicketMessage) => {

            // SetTimeout is here because without it the change of the subject value does not emit it's value to its subscribers
            this.deferEmission(() => {
                let messageAlert: Alert = {
                    assignedTo: user,
                    body: ticketMessage.body,
                    createdBy: ticketMessage.createdBy,
                    createdByName: ticketMessage.createdByName,
                    creationDate: ticketMessage.creationDate,
                    relatedData: 'Tickets',
                    relatedDataId: ticketMessage.ticketId,
                    highPriority: false,
                    id: ticketMessage.id ?? 0
                }

                this.messageSubject$.next(ticketMessage);
                this.addAlertToList(messageAlert);
            });

        })
    }

    stopListeningToAlerts() {
        this._pendingAlertTimeouts.forEach((handle) => clearTimeout(handle));
        this._pendingAlertTimeouts.clear();
        this._signalRService.closeConnection();
    }

    // Tracked so a pending emission can be cancelled when the connection is closed
    private deferEmission(action: () => void) {
        const handle = setTimeout(() => {
            this._pendingAlertTimeouts.delete(handle);
            action();
        }, 500);

        this._pendingAlertTimeouts.add(handle);
    }
}